import { regionSettingPreset } from '../../../shared/region-settings';
import type { RegionConfig } from '../../../shared/types/region';
import { regionFormat } from './region-format';

export type ZonedDateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

type FormatIsoRangeOptions = {
  fallbackTimezone?: string;
  omitYear?: boolean;
};

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

// The US preset default timezone (America/Chicago). Signed-in screens read the
// deployment's own value from useRegionConfig(); this is what shows before that
// arrives, on public pages with no session to fetch it, and in helpers called
// without a zone. Anything that lost its explicit timezone renders in the
// business's zone, never in a setter-side one.
export const DEFAULT_TIMEZONE = regionSettingPreset('US', 'defaultTimezone');

export function normalizeTimezone(value: string | null | undefined, fallback = DEFAULT_TIMEZONE): string {
  const candidate = String(value || '').trim() || fallback;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: candidate }).format(new Date());
    return candidate;
  } catch {
    return fallback;
  }
}

export function requireTimezone(value: string | null | undefined, fieldLabel = 'timezone'): string {
  const candidate = String(value || '').trim();
  if (!candidate) {
    throw new Error(`${fieldLabel} is missing`);
  }
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: candidate }).format(new Date());
    return candidate;
  } catch {
    throw new Error(`${fieldLabel} is invalid`);
  }
}

export function zonedDateParts(date: Date, timezone: string): ZonedDateParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: normalizeTimezone(timezone),
    // hourCycle only — hour12:false would override it and resolves to h24 on
    // older engines (midnight = "24", which corrupts the Date.UTC round-trip).
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const byType = new Map<string, string>();
  parts.forEach((part) => {
    if (part.type !== 'literal') {
      byType.set(part.type, part.value);
    }
  });
  return {
    year: Number(byType.get('year') || '0'),
    month: Number(byType.get('month') || '1'),
    day: Number(byType.get('day') || '1'),
    hour: Number(byType.get('hour') || '0'),
    minute: Number(byType.get('minute') || '0'),
    second: Number(byType.get('second') || '0'),
  };
}

function parseLocalDateTimeInput(value: string): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
} {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) {
    throw new Error('invalid datetime-local value');
  }
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
  };
}

function timezoneOffsetMs(utcDate: Date, timezone: string): number {
  const parts = zonedDateParts(utcDate, timezone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return asUtc - utcDate.getTime();
}

export function localDateTimeToUtcIso(localDateTime: string, timezone: string): string {
  const tz = normalizeTimezone(timezone);
  const parts = parseLocalDateTimeInput(localDateTime);
  const desiredUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0);

  let candidateUtc = desiredUtc;
  for (let i = 0; i < 4; i += 1) {
    const offset = timezoneOffsetMs(new Date(candidateUtc), tz);
    const nextCandidate = desiredUtc - offset;
    if (nextCandidate === candidateUtc) {
      break;
    }
    candidateUtc = nextCandidate;
  }

  return new Date(candidateUtc).toISOString();
}

/**
 * Reinterpret a date-range picker boundary as a day edge in the selected
 * timezone and return the UTC instant.
 *
 * The picker builds its boundaries with browser-local `new Date(...)` calls, so
 * `date`'s *browser-local calendar day* is the day the user meant. This reads
 * that day back and anchors it in `timezone` instead of the browser's zone:
 * `edge: 'start'` → 00:00 of the day, `edge: 'end'` → 00:00 of the following
 * day (the exclusive upper bound the insights queries use). Without this,
 * "This month" from a Manila browser viewing America/Chicago data starts a day
 * early and pulls in the prior period's rows.
 */
export function pickerDayBoundaryIso(date: Date, timezone: string, edge: 'start' | 'end'): string {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (edge === 'end') day.setDate(day.getDate() + 1);
  const wallClock = `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}T00:00`;
  return localDateTimeToUtcIso(wallClock, timezone);
}

export function utcIsoToLocalDateTimeInput(iso: string, timezone: string): string {
  const parts = zonedDateParts(new Date(iso), timezone);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}

export function formatIsoForTimezone(iso: string, timezone: string): string {
  try {
    return formatInTimezone(iso, timezone, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return `${formatInTimezone(iso, 'UTC', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })} UTC`;
  }
}

/**
 * "Jul 15, 2026, 3:00 PM CDT" — formatIsoForTimezone plus the DST-aware zone
 * abbreviation. Use wherever an instant is rendered in a timezone the reader
 * didn't pick per-record (activity lists, outcome warnings): an unlabeled time
 * gets read as the record's own local time (see Trello #271, meetings resolved
 * before they started).
 */
export function formatIsoWithZoneLabel(iso: string, timezone: string): string {
  return `${formatIsoForTimezone(iso, timezone)} ${formatTimezoneShortLabel(timezone, iso)}`;
}

/**
 * Date-only label ("Jul 7, 2026") anchored in `timezone`. Use for day columns
 * where rendering a UTC instant with browser-local `toLocaleDateString()` would
 * shift the day by the browser↔zone offset (the "booked today, shows yesterday"
 * bug for a Manila browser viewing US meetings).
 */
export function formatDateForTimezone(iso: string, timezone: string): string {
  try {
    return formatInTimezone(iso, timezone, { dateStyle: 'medium' });
  } catch {
    return `${formatInTimezone(iso, 'UTC', { dateStyle: 'medium' })} UTC`;
  }
}

function formatInTimezone(
  iso: string | Date,
  timezone: string,
  options: Intl.DateTimeFormatOptions
): string {
  const value = iso instanceof Date ? iso : new Date(iso);
  return new Intl.DateTimeFormat(undefined, {
    timeZone: normalizeTimezone(timezone),
    ...options,
  }).format(value);
}

export function formatClockForTimezone(iso: string | Date, timezone: string): string {
  return formatInTimezone(iso, timezone, {
    hour: 'numeric',
    minute: '2-digit',
    ...(regionFormat.twelveHourClock() ? { hour12: true } : { hourCycle: 'h23' }),
  });
}

/** 24-hour "HH:MM" wall clock in the given zone (attendance join/leave times). */
export function formatClockInTimezone(ms: number, timezone: string): string {
  return new Date(ms).toLocaleTimeString('en-GB', {
    timeZone: normalizeTimezone(timezone),
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
}

export function formatTimezoneShortLabel(
  timezone: string,
  referenceIso: string | Date = new Date(),
  countryCode?: string | null
): string {
  const normalized = normalizeTimezone(timezone);
  const value = referenceIso instanceof Date ? referenceIso : new Date(referenceIso);

  // Prefer the DST-aware abbreviation (EST/EDT, CST/CDT, PST/PDT) for the given
  // reference date over the static generic form (ET/CT/PT) so labels flip across
  // the daylight-savings boundary automatically. en-US has no abbreviations for
  // European zones; en-GB does (CET/CEST, EET/EEST).
  let shortLabel: string | undefined;
  for (const locale of ['en-US', 'en-GB']) {
    shortLabel = new Intl.DateTimeFormat(locale, {
      timeZone: normalized,
      timeZoneName: 'short',
    })
      .formatToParts(value)
      .find((part) => part.type === 'timeZoneName')
      ?.value
      .trim();

    if (shortLabel && !/^GMT/i.test(shortLabel)) {
      return shortLabel;
    }
  }

  // Zones without a distinct abbreviation (Intl returns "GMT+8" for them) fall
  // back to the generic name.
  const genericLabel = new Intl.DateTimeFormat('en-US', {
    timeZone: normalized,
    timeZoneName: 'shortGeneric',
  })
    .formatToParts(value)
    .find((part) => part.type === 'timeZoneName')
    ?.value
    .trim();

  if (genericLabel && !/^GMT/i.test(genericLabel) && !/Time$/i.test(genericLabel)) {
    return genericLabel;
  }

  if (countryCode) {
    return countryCode.toUpperCase();
  }

  return genericLabel || shortLabel || normalized.split('/').pop()?.replace(/_/g, ' ') || normalized;
}

export function formatRelativeToNow(targetIso: string, now = new Date()): string {
  const diffMs = new Date(targetIso).getTime() - now.getTime();
  const prefix = diffMs >= 0 ? 'In ' : '';
  const suffix = diffMs >= 0 ? '' : ' ago';
  let remaining = Math.abs(diffMs);
  const days = Math.floor(remaining / 86_400_000);
  remaining -= days * 86_400_000;
  const hours = Math.floor(remaining / 3_600_000);
  remaining -= hours * 3_600_000;
  const minutes = Math.floor(remaining / 60_000);

  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours || days) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return `${prefix}${parts.join(' ')}${suffix}`;
}

function sameCalendarDate(left: ZonedDateParts, right: ZonedDateParts): boolean {
  return left.year === right.year && left.month === right.month && left.day === right.day;
}

export function formatIsoRangeForTimezone(
  startIso: string,
  endIso: string,
  timezone: string,
  options?: FormatIsoRangeOptions
): string {
  const tz = normalizeTimezone(timezone, options?.fallbackTimezone);
  const start = new Date(startIso);
  const end = new Date(endIso);

  try {
    const startParts = zonedDateParts(start, tz);
    const endParts = zonedDateParts(end, tz);

    const fullFormatter = options?.omitYear
      ? new Intl.DateTimeFormat(undefined, {
          timeZone: tz,
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      : new Intl.DateTimeFormat(undefined, {
          timeZone: tz,
          dateStyle: 'medium',
          timeStyle: 'short',
        });
    const timeFormatter = new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      timeStyle: 'short',
    });

    if (sameCalendarDate(startParts, endParts)) {
      return `${fullFormatter.format(start)} - ${timeFormatter.format(end)}`;
    }
    return `${fullFormatter.format(start)} - ${fullFormatter.format(end)}`;
  } catch {
    return `${formatIsoForTimezone(startIso, tz)} - ${formatIsoForTimezone(endIso, tz)}`;
  }
}

// ── Timezone display: the standard pick list + abbreviation sugar ──────────

// Short business names for the zones pick-lists offer; any other zone reads as
// its city.
const TIMEZONE_NAMES: Record<string, string> = {
  'America/Chicago': 'Central',
  'America/New_York': 'Eastern',
  'America/Denver': 'Mountain',
  'America/Los_Angeles': 'Pacific',
};

function timezoneName(timeZone: string): string {
  return TIMEZONE_NAMES[timeZone] || timeZone.split('/').pop()?.replace(/_/g, ' ') || timeZone;
}

/** Pick-list options for a region's timezone choices (useTimezoneChoices), in order. */
export function timezoneChoiceOptions(zones: readonly string[]): { value: string; label: string }[] {
  return zones.map((value) => ({ value, label: timezoneName(value) }));
}

type RegionTimezones = Pick<RegionConfig, 'region' | 'timezoneChoices'>;

// Where closers and customers can be spans more of the US than the staff
// choices do.
export const US_AVAILABILITY_TIMEZONES: readonly { value: string; label: string }[] = [
  { value: 'America/New_York', label: 'US Eastern (New York)' },
  { value: 'America/Chicago', label: 'US Central (Chicago)' },
  { value: 'America/Denver', label: 'US Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'US Mountain - no DST (Phoenix)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'US Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'US Hawaii (Honolulu)' },
  { value: 'America/Puerto_Rico', label: 'US Atlantic (Puerto Rico)' },
];

const US_BOOKING_PORTAL_TIMEZONES: readonly { value: string; label: string }[] = [
  { value: 'America/New_York', label: 'Eastern' },
  { value: 'America/Chicago', label: 'Central' },
  { value: 'America/Denver', label: 'Mountain' },
  { value: 'America/Phoenix', label: 'Arizona' },
  { value: 'America/Los_Angeles', label: 'Pacific' },
  { value: 'America/Anchorage', label: 'Alaska' },
  { value: 'Pacific/Honolulu', label: 'Hawaii' },
];

/** The zones pinned above the full list on the availability page, hinted with the region. */
export function availabilityPinnedTimezones(config: RegionTimezones): { value: string; label: string; hint: string }[] {
  const zones = config.region === 'US' ? US_AVAILABILITY_TIMEZONES : timezoneChoiceOptions(config.timezoneChoices);
  return zones.map((zone) => ({ ...zone, hint: config.region }));
}

/** The booking portal's zone list: the customer's side of the region, not the staff choices. */
export function bookingPortalTimezones(
  config: Pick<RegionConfig, 'region' | 'defaultTimezone'>
): { value: string; label: string }[] {
  return config.region === 'US'
    ? [...US_BOOKING_PORTAL_TIMEZONES]
    : timezoneChoiceOptions([config.defaultTimezone]);
}

// Far-off zones in the region's choices belong in the pick-lists but not on
// the clock strip, which keeps to the choices within this many hours of its
// default timezone.
const CLOCK_STRIP_MAX_HOURS_FROM_DEFAULT = 3;

/** The always-visible clock strip's zones: the region's choices near its default timezone, west to east. */
export function clockStripTimezones(choices: readonly string[], defaultTimezone: string, at: Date): string[] {
  // Offsets on fixed winter and summer dates of `at`'s year, not at `at`
  // itself: zones change clocks an hour apart, so live offsets would reorder
  // the strip during a DST switch-over.
  const year = at.getUTCFullYear();
  const winter = new Date(Date.UTC(year, 0, 15, 12));
  const summer = new Date(Date.UTC(year, 6, 15, 12));
  const home = timezoneOffsetMs(winter, defaultTimezone);
  return choices
    .filter((zone) => Math.abs(timezoneOffsetMs(winter, zone) - home) <= CLOCK_STRIP_MAX_HOURS_FROM_DEFAULT * 3_600_000)
    .sort(
      (left, right) =>
        timezoneOffsetMs(winter, left) - timezoneOffsetMs(winter, right)
        || timezoneOffsetMs(summer, left) - timezoneOffsetMs(summer, right)
    );
}

/** "CDT" for now (or `at`) — sugar over formatTimezoneShortLabel. */
export function timezoneAbbreviation(timeZone: string, at: Date = new Date()): string {
  return formatTimezoneShortLabel(timeZone, at);
}

/**
 * "Central (CDT)" for the standard zones, "Phoenix (MST)" for the rest — the
 * pick-list and read-out label, with the live seasonal abbreviation.
 */
export function timezoneDisplayLabel(timeZone: string, at: Date = new Date()): string {
  const abbr = timezoneAbbreviation(normalizeTimezone(timeZone), at);
  const name = timezoneName(timeZone);
  return name === abbr ? abbr : `${name} (${abbr})`;
}
